import React, { useState } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase/config';

// Must match the companySlug helper in functions/index.js and Engagements.js
const companySlug = (name) => String(name || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

export default function Register({ onSwitch }) {
  const [step, setStep] = useState(1);
  const [code, setCode] = useState('');
  const [codeData, setCodeData] = useState(null);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '',
    confirmPassword: '', department: '', jobTitle: '', role: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const snap = await getDoc(doc(db, 'engagements', code.toUpperCase().trim()));
      if (!snap.exists()) {
        setError('That code is not valid. Check with your program coordinator.');
        setLoading(false);
        return;
      }
      const data = snap.data();
      if (!data.active) {
        setError('This code is no longer active. Contact your program coordinator.');
        setLoading(false);
        return;
      }
      if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
        setError('Registration for this program has closed. Contact your program coordinator.');
        setLoading(false);
        return;
      }
      // Shared seat pool check. Seats live at the company level and every
      // code for the company draws from the same pool, so a full pool blocks
      // here before the participant fills out the whole form.
      const slug = companySlug(data.company);
      if (slug) {
        const poolSnap = await getDoc(doc(db, 'companies', slug));
        if (poolSnap.exists()) {
          const pool = poolSnap.data();
          if (typeof pool.seatLimit === 'number' && (pool.seatsUsed || 0) >= pool.seatLimit) {
            setError('All seats for this program are currently filled. Contact your program coordinator to request more.');
            setLoading(false);
            return;
          }
        }
      }
      setCodeData({ ...data, code: code.toUpperCase().trim() });
      setStep(2);
    } catch {
      setError('Something went wrong verifying your code. Please try again.');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);

    let cred = null;

    // Step 1: Create the Firebase auth account
    try {
      cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await updateProfile(cred.user, { displayName: `${form.firstName} ${form.lastName}` });
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Try signing in.');
      } else {
        setError('Could not create your account. Please try again.');
      }
      setLoading(false);
      return;
    }

    // Step 2: Claim a seat from the shared company pool. This runs server
    // side in a transaction so two people registering at the same moment
    // cannot both slip past the cap. If the pool filled up between the code
    // check and now, we roll back the auth account so the email is not
    // stranded, then surface the seats-full message.
    try {
      const claimSeat = httpsCallable(getFunctions(), 'claimSeat');
      await claimSeat({ companyCode: codeData.code });
    } catch (err) {
      try { await cred.user.delete(); } catch { /* best effort rollback */ }
      if (err.code === 'functions/resource-exhausted') {
        setError('All seats for this program are currently filled. Contact your program coordinator to request more.');
      } else {
        setError('Could not reserve your seat. Please try again, or contact your program coordinator if it keeps happening.');
      }
      setLoading(false);
      return;
    }

    // Step 3: Write the user profile to Firestore with company assignment
    // This is a separate try/catch so we know exactly which step failed
    try {
      await setDoc(doc(db, 'users', cred.user.uid), {
        displayName: `${form.firstName} ${form.lastName}`,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        company: codeData.company,
        companyCode: codeData.code,
        engagementId: codeData.code,
        department: form.department,
        jobTitle: form.jobTitle,
        role: form.role || '',
        registeredAt: serverTimestamp(),
        accessExpiresAt: codeData.accessExpiresAt
          ? (codeData.accessExpiresAt.toDate ? codeData.accessExpiresAt.toDate() : new Date(codeData.accessExpiresAt))
          : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      });
    } catch (err) {
      // Auth account was created but profile write failed.
      // Surface a specific error so this is diagnosable.
      console.error('Firestore profile write failed:', err);
      setError(
        'Your account was created but your company profile could not be saved. ' +
        'Please contact your program coordinator and provide this error: ' +
        err.code || 'profile-write-failed'
      );
      setLoading(false);
      return;
    }

    setLoading(false);
    // Registration complete — App.js auth state listener will pick up the new user
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={styles.screen}>
      <div style={styles.box}>
        <div style={styles.eyebrow}>The Corporate Fixers</div>
        <div style={styles.title}>Create Your<br />Account</div>
        <div style={styles.subtitle}>From Curious to Capable</div>

        {step === 1 && (
          <form onSubmit={handleCodeSubmit}>
            <div style={styles.stepLabel}>Step 1 of 2 — Enter your company access code</div>
            <div style={styles.field}>
              <label style={styles.label}>Company Access Code</label>
              <input
                style={{ ...styles.input, textTransform: 'uppercase', letterSpacing: '3px', fontSize: '18px', textAlign: 'center' }}
                type="text"
                placeholder="e.g. ACME-2026"
                value={code}
                onChange={e => setCode(e.target.value)}
                required
              />
            </div>
            <div style={styles.codeNote}>Your program coordinator provided this code. It is case-insensitive.</div>
            {error && <div style={styles.error}>{error}</div>}
            <button style={styles.btn} type="submit" disabled={loading}>
              {loading ? 'Checking...' : 'Verify Code →'}
            </button>
          </form>
        )}

        {step === 2 && codeData && (
          <form onSubmit={handleRegister}>
            <div style={styles.companyBadge}>{codeData.company}</div>
            <div style={styles.stepLabel}>Step 2 of 2 — Create your account</div>
            <div style={styles.fieldRow}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>First Name</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="First"
                  value={form.firstName}
                  onChange={e => set('firstName', e.target.value)}
                  required
                />
              </div>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Last Name</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="Last"
                  value={form.lastName}
                  onChange={e => set('lastName', e.target.value)}
                  required
                />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Work Email</label>
              <input
                style={styles.input}
                type="email"
                placeholder="your@company.com"
                value={form.email}
                onChange={e => set('email', e.target.value)}
                required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Department</label>
              <input
                style={styles.input}
                type="text"
                placeholder="e.g. HR, Marketing, Finance"
                value={form.department}
                onChange={e => set('department', e.target.value)}
                required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Job Title</label>
              <input
                style={styles.input}
                type="text"
                placeholder="e.g. HR Manager, Marketing Director"
                value={form.jobTitle}
                onChange={e => set('jobTitle', e.target.value)}
                required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Role / Level <span style={{ fontWeight: 300, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
              <input
                style={styles.input}
                type="text"
                placeholder="e.g. Manager, Individual Contributor, Director"
                value={form.role}
                onChange={e => set('role', e.target.value)}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="At least 8 characters"
                value={form.password}
                onChange={e => set('password', e.target.value)}
                required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Confirm Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="Confirm your password"
                value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)}
                required
              />
            </div>
            {error && <div style={styles.error}>{error}</div>}
            <button style={styles.btn} type="submit" disabled={loading}>
              {loading ? 'Creating account...' : 'Create My Account →'}
            </button>
          </form>
        )}

        <div style={styles.switchRow}>
          Already have an account?{' '}
          <span style={styles.switchLink} onClick={onSwitch}>Sign in</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  screen: { background: '#1B2A4A', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  box: { background: '#FAF8F5', width: '100%', maxWidth: '440px', padding: '48px 40px' },
  eyebrow: { fontSize: '10px', fontWeight: '600', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#C0392B', marginBottom: '6px', fontFamily: 'DM Sans, sans-serif' },
  title: { fontFamily: 'Playfair Display, serif', fontSize: '30px', fontWeight: '900', color: '#1B2A4A', lineHeight: '1.2', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#8A8A8A', fontWeight: '300', marginBottom: '24px', fontFamily: 'DM Sans, sans-serif', fontStyle: 'italic' },
  stepLabel: { fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '16px', fontFamily: 'DM Sans, sans-serif' },
  companyBadge: { background: '#1B2A4A', color: '#D4A847', padding: '8px 16px', fontSize: '13px', fontWeight: '600', marginBottom: '16px', display: 'inline-block', letterSpacing: '1px' },
  field: { marginBottom: '14px' },
  fieldRow: { display: 'flex', gap: '12px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '5px', fontFamily: 'DM Sans, sans-serif' },
  input: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '14px', background: '#FFFFFF', color: '#3D3D3D', outline: 'none', boxSizing: 'border-box' },
  codeNote: { fontSize: '12px', color: '#8A8A8A', marginBottom: '16px', fontFamily: 'DM Sans, sans-serif', fontWeight: '300' },
  error: { background: '#fdecea', color: '#a32d2d', padding: '10px 14px', fontSize: '13px', marginBottom: '12px', fontFamily: 'DM Sans, sans-serif' },
  btn: { width: '100%', background: '#1B2A4A', color: '#FFFFFF', border: 'none', padding: '14px', fontFamily: 'Playfair Display, serif', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginTop: '8px' },
  switchRow: { textAlign: 'center', marginTop: '20px', fontSize: '12px', color: '#8A8A8A', fontFamily: 'DM Sans, sans-serif' },
  switchLink: { color: '#C0392B', cursor: 'pointer', textDecoration: 'underline', marginLeft: '4px' },
};
